import { useEffect, useState } from 'react';
import { VehiclePlan } from '../types/vehicle';
import { useSubmit } from 'react-router';

export const findPlanIdByMileage = (
  vehiclePlans: VehiclePlan[],
  mileage?: number
): string | undefined => {
  return vehiclePlans?.find((plan) => plan.miles_per_month === mileage)?.id?.toString();
};

interface UsePDPSelectPlanSubmitProps {
  vehiclePlans: VehiclePlan[];
  selectedMileagePlan?: number;
  hasSelectedPDPPlan?: boolean;
  postSubmitDelegate?: (event: string) => void;
  DEFAULT_PDP_PLAN_SELECTION_COMING_FROM_INVENTORY_ENABLED?: boolean;
}

/**
 * Hook to organize the logic for handling the selection of a plan in the PDP.
 * @param vehiclePlans plans of vehicle
 * @param selectedMileagePlan Number of miles per month for plan
 * @param hasSelectedPDPPlan Whether a plan has been selected before
 * @param postSubmitDelegate delegate function to call after submit occurs to react-router
 * @param DEFAULT_PDP_PLAN_SELECTION_COMING_FROM_INVENTORY_ENABLED Whether to enable logic for defaulting plan selection after coming from inventory page
 * @returns
 */
export default function usePDPSelectPlanSubmit({
  vehiclePlans,
  selectedMileagePlan,
  hasSelectedPDPPlan,
  postSubmitDelegate,
  DEFAULT_PDP_PLAN_SELECTION_COMING_FROM_INVENTORY_ENABLED = false,
}: UsePDPSelectPlanSubmitProps) {
  const submit = useSubmit();

  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>(
    // Only select a plan by default on PDP, if the feature flag for that feature is enabled or
    // (when that FF is disabled) if the flag for having already selected a PDP plan before is true.
    DEFAULT_PDP_PLAN_SELECTION_COMING_FROM_INVENTORY_ENABLED || hasSelectedPDPPlan
      ? findPlanIdByMileage(vehiclePlans, selectedMileagePlan)
      : undefined
  );

  // When the selectedMileagePlan changes, update the selectedPlanId to effectively reset the state of
  // selectedPlanId to the initial state. This is necessary for scenarios where the selectedMileagePlan
  // gets changed outside of the handleSelectPlan function and we need to keep the session cart value and
  // selected plan in the UI in sync. This will still need to have the default plan selection logic
  // applied to it.
  useEffect(() => {
    setSelectedPlanId(
      DEFAULT_PDP_PLAN_SELECTION_COMING_FROM_INVENTORY_ENABLED || hasSelectedPDPPlan
        ? findPlanIdByMileage(vehiclePlans, selectedMileagePlan)
        : undefined
    );
  }, [
    DEFAULT_PDP_PLAN_SELECTION_COMING_FROM_INVENTORY_ENABLED,
    hasSelectedPDPPlan,
    selectedMileagePlan,
    vehiclePlans,
  ]);

  const handleSelectPlan = (event: string) => {
    const selectedPlanMileage = vehiclePlans.find(
      (plan) => plan.id === parseInt(event)
    )?.miles_per_month;

    if (selectedPlanMileage !== undefined) {
      submit(
        {
          intent: 'update_selected_plan_mileage',
          plan_mileage: selectedPlanMileage,
        },
        {
          method: 'post',
        }
      );
      setSelectedPlanId(event);
      postSubmitDelegate?.(event);
    }
  };

  return { handleSelectPlan, selectedPlanId };
}
